'use strict'
import React, { useState, useRef, useEffect } from 'react'
import '../../../../assets/css/style.css'
import '../../../../assets/css/common.css'

/**
 * @typedef {Object} SearchableDropdownProps
 * @property {{ value: string; label: string }[]} options
 * @property {string} value
 * @property {function(string): void} onChange
 * @property {string} placeholder
 * @property {boolean} disabled
 * @property {string} className
 */

/**
 * SearchableDropdownコンポーネント 検索機能が統合されたプルダウンコンポーネント
 *
 * @param {SearchableDropdownProps} props
 */
const SearchableDropdown = ({
  options,
  value,
  onChange,
  placeholder = '選択してください',
  disabled = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [searchKeyword, setSearchKeyword] = useState('')
  const dropdownRef = useRef(null)
  const searchInputRef = useRef(null)

  // 選択中のラベルを取得
  const selectedLabel = options.find((opt) => opt.value === value)?.label || placeholder

  // フィルタリングされたオプション
  const filteredOptions = options.filter((opt) => {
    if (!searchKeyword) return true
    return opt.label.toLowerCase().includes(searchKeyword.toLowerCase())
  })

  // 外部クリックで閉じる
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false)
        setSearchKeyword('')
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
      // 検索入力欄にフォーカス
      setTimeout(() => {
        searchInputRef.current?.focus()
      }, 100)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  // ESCキーで閉じる
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false)
        setSearchKeyword('')
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => {
      document.removeEventListener('keydown', handleEscape)
    }
  }, [isOpen])

  const handleToggle = () => {
    if (disabled) return
    setIsOpen(!isOpen)
    if (!isOpen) {
      setSearchKeyword('')
    }
  }

  const handleSelect = (optionValue) => {
    onChange(optionValue)
    setIsOpen(false)
    setSearchKeyword('')
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* トリガーボタン */}
      <button
        type="button"
        className={`form-select w-full px-3 py-2 border rounded-md text-sm bg-white text-left flex items-center justify-between ${
          disabled ? 'bg-gray-100 opacity-60 cursor-not-allowed' : 'cursor-pointer hover:border-blue2'
        } ${value ? 'border-green-500 bg-green-50' : 'border-gray-300'}`}
        onClick={handleToggle}
        disabled={disabled}
      >
        <span className={value ? 'text-blue2-darken-20' : 'text-gray-400'}>{selectedLabel}</span>
        <span className={`transition-transform ${isOpen ? 'rotate-180' : ''}`}>▼</span>
      </button>

      {/* ドロップダウン */}
      {isOpen && !disabled && (
        <div className="absolute z-30 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-60 overflow-hidden">
          {/* 検索入力欄 */}
          <div className="p-2 border-b border-gray-200">
            <input
              ref={searchInputRef}
              type="text"
              className="text-input text-left w-full px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue2"
              placeholder="検索..."
              value={searchKeyword}
              onChange={(e) => setSearchKeyword(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          {/* オプションリスト */}
          <div className="max-h-48 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-gray-500 text-center">該当する項目がありません</div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  className={`w-full px-3 py-2 text-left text-sm hover:bg-blue2-lighten-50 transition-colors ${
                    option.value === value ? 'bg-blue2-lighten-40 text-blue2-darken-20' : 'text-gray-700'
                  }`}
                  onClick={() => handleSelect(option.value)}
                >
                  {option.label}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default SearchableDropdown
